import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { SubLocation } from '@ups/xplat/api/dto';
import { WarehouseInventoryService } from '@ups/xplat/api/services';
import {
  environment,
  MyHttpClientFactory,
  ResponseCasingEnum,
} from '@ups/xplat/core';
import { Observable, map } from 'rxjs';
import {
  SublocationScope,
  SublocationSearchOptions,
  searchSublocationNames,
} from '../utils/sublocation-search.utils';

@Injectable({
  providedIn: 'root',
})
export class SearchSublocationService {
  private readonly http: HttpClient;

  constructor(
    public warehouseInventoryService: WarehouseInventoryService,
    clientFactory: MyHttpClientFactory
  ) {
    // Same client WarehouseInventoryService uses for this endpoint.
    this.http = clientFactory.createHttpClient(
      environment.urls.toolTrackerAPI,
      true,
      ResponseCasingEnum.CamelCase
    );
  }

  /** Cached sub-location names for the warehouse a page last loaded. */
  readonly names$ = this.warehouseInventoryService.sublocations$.pipe(
    map((sublocations) =>
      (sublocations ?? []).map((entry) => entry.sublocationName)
    )
  );

  search(searchString: string, offSite: boolean) {
    return this.searchWith(searchString, { offSite });
  }

  searchOnSite(searchString: string) {
    return this.search(searchString, false);
  }

  searchOffSite(searchString: string) {
    return this.search(searchString, true);
  }

  /**
   * WI 34968: the scope warehouse's names, fetched for the free-text control alone. It never
   * reads or writes the shared sublocations$ cache: that may hold another warehouse, and a live
   * tab page (Transfer) is using it. Errors propagate so the control can say the load failed.
   */
  namesFor(scope: SublocationScope): Observable<Array<string>> {
    return this.http
      .get<Array<SubLocation>>(
        `/api/warehouseInventory/sublocation/${scope.companyId}/${scope.warehouseId}`
      )
      .pipe(
        map((rows) =>
          (rows ?? [])
            .map((row) => row.sublocationName)
            .filter((name) => !!name)
        )
      );
  }

  validate(searchString: string) {
    return this.warehouseInventoryService.sublocations$.pipe(
      map((sublocations) => {
        const locations = sublocations
          .map((entry) => entry.sublocationName)
          .filter((location) => location !== '')
          .find(
            (location) => location.toLowerCase() === searchString.toLowerCase()
          );

        return locations;
      })
    );
  }

  private searchWith(searchString: string, opts: SublocationSearchOptions) {
    return this.names$.pipe(
      map((names) => searchSublocationNames(names, searchString, opts))
    );
  }
}
